import { useRef } from "react";
import { Box, Flex, Text, Heading, Badge, Image, Icon } from "@chakra-ui/react";
import { motion, useScroll, useTransform, useReducedMotion, MotionValue } from "framer-motion";
import { FaThLarge, FaBriefcase, FaTasks, FaRegClock, FaUsers, FaFileAlt, FaUpload } from "react-icons/fa";
import type { IconType } from "react-icons";
import Reveal, { MotionBox } from "../../components/motion";
import { BobContent, BobScene } from "../../data/bobContent";
import bobLockupDark from "../../assets/logo/bob/bob-lockup-dark.svg";

// The demo walks the scenes in exactly the real app's sidebar order —
// Dashboard, Partners, Tasks, Attendance, Employees, Documents — so
// scrolling through it mirrors clicking down the actual nav. Departments &
// roles lives inside the Employees module and Shift schedule is a tab
// inside Attendance in the real product, so both share their parent's nav
// index and icon.
const NAV_INDEX: Record<string, number> = {
  dashboard: 0,
  partners: 1,
  tasks: 2,
  schedule: 3,
  shifts: 3,
  employees: 4,
  roles: 4,
  documents: 5,
};

const NAV_ICONS: IconType[] = [FaThLarge, FaBriefcase, FaTasks, FaRegClock, FaUsers, FaFileAlt];

const AVATAR_PALETTE = ["#5b6f55", "#7c6fd6", "#2f8f83", "#c9a227", "#8a8a8a", "#b56576"];

const initials = (name: string) =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ name, index }: { name: string; index: number }) => (
  <Flex
    w="30px"
    h="30px"
    borderRadius="full"
    align="center"
    justify="center"
    flexShrink={0}
    bg={AVATAR_PALETTE[index % AVATAR_PALETTE.length]}
    color="white"
    fontSize="11px"
    fontWeight="700"
  >
    {initials(name)}
  </Flex>
);

const Pill = ({
  children,
  tone = "chalk",
}: {
  children: React.ReactNode;
  tone?: "bob" | "chalk" | "amber" | "lavender" | "outline";
}) => (
  <Badge
    px={2.5}
    py={0.5}
    borderRadius="full"
    fontSize="xs"
    fontWeight="600"
    textTransform="none"
    border={tone === "outline" ? "1px solid" : "none"}
    borderColor="chalk.500"
    bg={
      tone === "bob"
        ? "bob.100"
        : tone === "amber"
        ? "#FCE8C6"
        : tone === "lavender"
        ? "#E7E1F7"
        : tone === "outline"
        ? "transparent"
        : "chalk.300"
    }
    color={
      tone === "bob"
        ? "bob.900"
        : tone === "amber"
        ? "#8A5A12"
        : tone === "lavender"
        ? "#5B4C93"
        : "ink.900"
    }
  >
    {children}
  </Badge>
);

// Company/personal emails in the demo data are fictitious, but styled as
// deliberately unreadable anyway — the same care a real product screenshot
// of customer data would need, so the marketing page never sets the
// expectation that real partner data is fine to display in the open.
const BlurredEmail = ({ email }: { email: string }) => (
  <Text fontSize="xs" color="slate.500" filter="blur(4px)" userSelect="none" pointerEvents="none" noOfLines={1}>
    {email}
  </Text>
);

const SceneHeading = ({ title }: { title: string }) => (
  <Flex align="center" gap={2} mb={5}>
    <Heading as="h4" fontFamily="heading" fontSize="xl" color="ink.900">
      {title}
    </Heading>
    <Flex
      w="16px"
      h="16px"
      borderRadius="full"
      border="1px solid"
      borderColor="slate.400"
      align="center"
      justify="center"
      fontSize="9px"
      color="slate.400"
      flexShrink={0}
    >
      i
    </Flex>
  </Flex>
);

const DemoChrome = ({
  demo,
  scene,
  children,
}: {
  demo: BobContent["demo"];
  scene: BobScene;
  children: React.ReactNode;
}) => {
  const activeNav = NAV_INDEX[scene.id];
  return (
    <Flex
      h="100%"
      borderRadius="xl"
      overflow="hidden"
      border="1px solid"
      borderColor="chalk.400"
      boxShadow="0 24px 70px rgba(19, 78, 74, 0.18)"
      bg="white"
    >
      <Flex
        direction="column"
        w="180px"
        flexShrink={0}
        position="relative"
        overflow="hidden"
        bgGradient="linear(to-b, bob.800, bob.900)"
        px={4}
        py={5}
        display={{ base: "none", lg: "flex" }}
      >
        <Image src={bobLockupDark} alt="bob" w="82px" mb={0.5} position="relative" zIndex={1} />
        <Text
          fontSize="7px"
          fontWeight="700"
          letterSpacing="0.12em"
          color="whiteAlpha.500"
          textTransform="uppercase"
          mb={5}
          position="relative"
          zIndex={1}
        >
          {demo.tagline}
        </Text>
        <Box h="1px" bg="whiteAlpha.200" mb={4} position="relative" zIndex={1} />
        <Box mb={5} position="relative" zIndex={1}>
          <Text fontSize="9px" fontWeight="700" letterSpacing="0.08em" color="whiteAlpha.500" textTransform="uppercase" mb={1}>
            {demo.workspaceLabel}
          </Text>
          <Text fontSize="sm" fontWeight="700" color="white" noOfLines={1}>
            {demo.workspaceName}
          </Text>
        </Box>
        <Flex direction="column" gap={1} position="relative" zIndex={1}>
          {demo.nav.map((label, i) => (
            <Flex
              key={label}
              align="center"
              gap={2.5}
              px={3}
              py={2}
              position="relative"
              borderRadius="lg"
              fontSize="xs"
              fontWeight={i === activeNav ? "700" : "500"}
              color={i === activeNav ? "bob.300" : "whiteAlpha.700"}
              bg={i === activeNav ? "rgba(20, 184, 166, 0.14)" : "transparent"}
            >
              {i === activeNav && (
                <Box position="absolute" left="-16px" top="15%" bottom="15%" w="3px" bg="bob.300" borderRadius="full" />
              )}
              <Icon as={NAV_ICONS[i]} boxSize="11px" />
              <Text noOfLines={1}>{label}</Text>
            </Flex>
          ))}
        </Flex>

        {/* Decorative network graphic, echoing the real app's sidebar footer */}
        <Box position="absolute" left="-10px" bottom="-10px" w="160px" h="140px" opacity={0.5} zIndex={0}>
          <svg viewBox="0 0 160 140" width="100%" height="100%" fill="none">
            <path
              d="M8 128 L48 78 L82 100 L128 60"
              stroke="#14b8a6"
              strokeOpacity="0.35"
              strokeWidth="1.5"
              fill="none"
            />
            {[
              [8, 128],
              [48, 78],
              [82, 100],
              [128, 60],
            ].map(([cx, cy]) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#14b8a6" fillOpacity="0.6" />
            ))}
          </svg>
        </Box>
      </Flex>

      <Flex direction="column" flex="1" minW={0}>
        <Flex
          justify="flex-end"
          align="center"
          px={{ base: 4, md: 5 }}
          py={3}
          borderBottom="1px solid"
          borderColor="chalk.300"
          flexShrink={0}
        >
          <Flex align="center" gap={3} flexShrink={0}>
            <Box
              fontSize="10px"
              px={2}
              py={0.5}
              border="1px solid"
              borderColor="bob.300"
              borderRadius="full"
              color="bob.300"
              display={{ base: "none", sm: "block" }}
            >
              {demo.langLabel}
            </Box>
            <Flex align="center" gap={2}>
              <Avatar name={demo.userName} index={2} />
              <Box display={{ base: "none", md: "block" }}>
                <Text fontSize="xs" fontWeight="700" color="ink.900" noOfLines={1}>
                  {demo.userName}
                </Text>
                <Text fontSize="10px" color="slate.500" noOfLines={1}>
                  {demo.userRole}
                </Text>
              </Box>
            </Flex>
          </Flex>
        </Flex>
        <Box flex="1" p={{ base: 4, md: 6 }} overflow="auto">
          {children}
        </Box>
      </Flex>
    </Flex>
  );
};

const ScenePanel = ({ scene, demo }: { scene: BobScene; demo: BobContent["demo"] }) => {
  let content: React.ReactNode;

  switch (scene.id) {
    case "employees":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Flex gap={2} mb={4}>
            {scene.tabs.map((tab, i) => (
              <Box
                key={tab}
                px={3}
                py={1.5}
                borderRadius="md"
                fontSize="xs"
                fontWeight="600"
                bg={i === 0 ? "bob.900" : "chalk.200"}
                color={i === 0 ? "white" : "slate.700"}
              >
                {tab}
              </Box>
            ))}
          </Flex>
          <Flex direction="column" gap={2}>
            {scene.rows.map((row, i) => (
              <Flex
                key={row.name}
                justify="space-between"
                align="center"
                gap={3}
                py={2.5}
                px={3}
                border="1px solid"
                borderColor="chalk.300"
                borderRadius="lg"
              >
                <Flex align="center" gap={3} minW={0}>
                  <Avatar name={row.name} index={i} />
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                      {row.name}
                    </Text>
                    <Text fontSize="xs" color="slate.500" noOfLines={1}>
                      {row.role} · {row.department}
                    </Text>
                  </Box>
                </Flex>
                <Pill tone="bob">{row.type === "employee" ? scene.typeLabels[0] : scene.typeLabels[1]}</Pill>
              </Flex>
            ))}
          </Flex>
        </>
      );
      break;

    case "roles":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Flex direction="column" gap={5}>
            {scene.departments.map((dept) => (
              <Box key={dept.name}>
                <Text fontSize="xs" fontWeight="700" color="slate.500" textTransform="uppercase" letterSpacing="0.04em" mb={2}>
                  {dept.name}
                </Text>
                <Flex gap={2} wrap="wrap">
                  {dept.roles.map((role) => (
                    <Pill key={role} tone="bob">
                      {role}
                    </Pill>
                  ))}
                </Flex>
              </Box>
            ))}
          </Flex>
        </>
      );
      break;

    case "schedule":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Box overflowX="auto">
            <Flex direction="column" gap={5} minW="480px">
              {scene.people.map((person, i) => (
                <Box key={person.name}>
                  <Flex justify="space-between" align="center" mb={2.5}>
                    <Flex align="center" gap={2.5}>
                      <Avatar name={person.name} index={i} />
                      <Text fontSize="sm" fontWeight="600">
                        {person.name}
                      </Text>
                    </Flex>
                    <Pill tone={person.today.toLowerCase().includes("leave") || person.today.includes("szabadság") ? "amber" : "bob"}>
                      {person.today}
                    </Pill>
                  </Flex>
                  <Flex gap={1.5} pl="42px">
                    {person.days.map((d) => (
                      <Box
                        key={d.day}
                        flex="1"
                        textAlign="center"
                        borderRadius="md"
                        py={1.5}
                        border="1px solid"
                        borderColor={d.working ? "bob.300" : "chalk.400"}
                        bg={d.working ? "bob.50" : "transparent"}
                        opacity={d.working ? 1 : 0.55}
                      >
                        <Text fontSize="10px" fontWeight="700" color="ink.900">
                          {d.day}
                        </Text>
                        <Text fontSize="9px" color="slate.600">
                          {d.working ? d.hours : "—"}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </Box>
              ))}
            </Flex>
          </Box>
        </>
      );
      break;

    case "shifts":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Box overflowX="auto">
            <Flex direction="column" gap={2} minW="480px">
              <Flex gap={2} pl="150px">
                {scene.days.map((d) => (
                  <Text key={d} flex="1" textAlign="center" fontSize="xs" fontWeight="700" color="slate.500">
                    {d}
                  </Text>
                ))}
              </Flex>
              {scene.rows.map((row) => (
                <Flex key={row.role} gap={2} align="center">
                  <Text w="150px" fontSize="xs" fontWeight="700" color="slate.500" textTransform="uppercase" letterSpacing="0.03em" flexShrink={0}>
                    {row.role}
                  </Text>
                  {row.cells.map((cell, i) => (
                    <Flex
                      key={i}
                      flex="1"
                      justify="center"
                      align="center"
                      borderRadius="full"
                      py={1.5}
                      border="1px solid"
                      borderColor={cell ? "bob.300" : "#F3B49B"}
                      bg={cell ? "bob.50" : "#FCE1D5"}
                    >
                      <Text fontSize="10px" fontWeight="600" color={cell ? "bob.900" : "#A8442A"} noOfLines={1}>
                        {cell ? cell.person.split(" ")[0] : "—"}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              ))}
              <Box mt={2} px={3} py={2} bg="#FCE8C6" borderRadius="md">
                <Text fontSize="xs" fontWeight="600" color="#8A5A12">
                  ⚠ {scene.warning}
                </Text>
              </Box>
            </Flex>
          </Box>
        </>
      );
      break;

    case "partners":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Box overflowX="auto" mb={5}>
            <Flex direction="column" gap={0} minW="520px">
              <Flex gap={3} px={1} pb={2} borderBottom="1px solid" borderColor="chalk.400">
                <Text flex="3" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase">
                  {scene.columns.company}
                </Text>
                <Text flex="2" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" display={{ base: "none", md: "block" }}>
                  {scene.columns.city}
                </Text>
                <Text flex="2" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase">
                  {scene.columns.email}
                </Text>
                <Text flex="1" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" textAlign="right" whiteSpace="nowrap">
                  {scene.columns.status}
                </Text>
              </Flex>
              {scene.partners.map((p) => (
                <Flex key={p.name} gap={3} align="center" py={3} borderBottom="1px solid" borderColor="chalk.300">
                  <Text flex="3" fontSize="sm" fontWeight="600" noOfLines={1}>
                    {p.name}
                  </Text>
                  <Text flex="2" fontSize="xs" color="slate.500" noOfLines={1} display={{ base: "none", md: "block" }}>
                    {p.city}
                  </Text>
                  <Box flex="2">
                    <BlurredEmail email={p.email} />
                  </Box>
                  <Flex flex="1" justify="flex-end" flexShrink={0}>
                    <Pill tone={p.status.toLowerCase().includes("akt") || p.status === "Active" ? "bob" : "lavender"}>{p.status}</Pill>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color="slate.500" mb={1.5}>
              {scene.activityHeading}
            </Text>
            <Flex direction="column" gap={1}>
              {scene.activities.map((a, i) => (
                <Flex key={i} justify="space-between" fontSize="xs" gap={2}>
                  <Text color="ink.900">
                    <Text as="span" fontWeight="700">
                      {a.type}:
                    </Text>{" "}
                    {a.text}
                  </Text>
                  <Text color="slate.500" flexShrink={0}>
                    {a.date}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Box>
        </>
      );
      break;

    case "tasks":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Flex gap={2} mb={4} wrap="wrap">
            <Box px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="600" bg="bob.900" color="white">
              {scene.toggleLabels[0]}
            </Box>
            <Box px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="600" bg="chalk.200" color="slate.700">
              {scene.toggleLabels[1]}
            </Box>
            <Box px={3} py={1.5} borderRadius="md" fontSize="xs" border="1px solid" borderColor="chalk.400" color="slate.600">
              {scene.filterLabels[0]}
            </Box>
            <Box px={3} py={1.5} borderRadius="md" fontSize="xs" border="1px solid" borderColor="chalk.400" color="slate.600">
              {scene.filterLabels[1]}
            </Box>
          </Flex>
          <Box overflowX="auto">
            <Flex direction="column" gap={0} minW="560px">
              <Flex gap={3} px={1} pb={2} borderBottom="1px solid" borderColor="chalk.400">
                <Text flex="3" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase">
                  {scene.columns.title}
                </Text>
                <Text flex="2" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" display={{ base: "none", md: "block" }}>
                  {scene.columns.partner}
                </Text>
                <Text flex="1" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" whiteSpace="nowrap">
                  {scene.columns.due}
                </Text>
                <Text flex="1" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" textAlign="right" whiteSpace="nowrap">
                  {scene.columns.status}
                </Text>
              </Flex>
              {scene.rows.map((t) => (
                <Flex key={t.title} gap={3} align="center" py={3} borderBottom="1px solid" borderColor="chalk.300">
                  <Text flex="3" fontSize="sm" fontWeight="600" noOfLines={1}>
                    {t.title}
                  </Text>
                  <Text flex="2" fontSize="xs" color="bob.300" noOfLines={1} display={{ base: "none", md: "block" }}>
                    {t.partner}
                  </Text>
                  <Text flex="1" fontSize="xs" color="slate.500" whiteSpace="nowrap">
                    {t.due}
                  </Text>
                  <Flex flex="1" justify="flex-end" flexShrink={0}>
                    <Pill tone="amber">{t.status}</Pill>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Box>
        </>
      );
      break;

    case "documents":
      content = (
        <>
          <Flex justify="space-between" align="start" mb={5} gap={3}>
            <SceneHeading title={scene.panelTitle} />
            <Flex
              align="center"
              gap={2}
              px={3}
              py={1.5}
              borderRadius="md"
              bg="bob.900"
              color="white"
              fontSize="xs"
              fontWeight="600"
              flexShrink={0}
            >
              <Icon as={FaUpload} boxSize="10px" />
              <Text noOfLines={1}>{scene.uploadLabel}</Text>
            </Flex>
          </Flex>
          <Flex gap={2} mb={4}>
            {scene.tabs.map((tab, i) => (
              <Box
                key={tab}
                px={3}
                py={1.5}
                borderRadius="md"
                fontSize="xs"
                fontWeight="600"
                bg={i === 0 ? "bob.900" : "chalk.200"}
                color={i === 0 ? "white" : "slate.700"}
              >
                {tab}
              </Box>
            ))}
          </Flex>
          <Box overflowX="auto">
            <Flex direction="column" gap={0} minW="520px">
              <Flex gap={3} px={1} pb={2} borderBottom="1px solid" borderColor="chalk.400">
                <Text flex="3" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase">
                  {scene.columns.file}
                </Text>
                <Text flex="1" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" display={{ base: "none", md: "block" }}>
                  {scene.columns.size}
                </Text>
                <Text flex="2" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" display={{ base: "none", md: "block" }}>
                  {scene.columns.uploadedBy}
                </Text>
                <Text flex="1" fontSize="10px" fontWeight="700" color="slate.500" textTransform="uppercase" textAlign="right" whiteSpace="nowrap">
                  {scene.columns.status}
                </Text>
              </Flex>
              {scene.rows.map((d) => (
                <Flex key={d.file} gap={3} align="center" py={3} borderBottom="1px solid" borderColor="chalk.300">
                  <Text flex="3" fontSize="sm" fontWeight="600" color="bob.300" noOfLines={1}>
                    {d.file}
                  </Text>
                  <Text flex="1" fontSize="xs" color="slate.500" display={{ base: "none", md: "block" }}>
                    {d.size}
                  </Text>
                  <Text flex="2" fontSize="xs" color="slate.500" noOfLines={1} display={{ base: "none", md: "block" }}>
                    {d.uploadedBy}
                  </Text>
                  <Flex flex="1" justify="flex-end" flexShrink={0}>
                    <Pill tone="bob">{d.status}</Pill>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Box>
        </>
      );
      break;

    case "dashboard":
      content = (
        <>
          <SceneHeading title={scene.panelTitle} />
          <Flex direction="column" gap={5}>
            <Flex gap={3} wrap="wrap">
              {scene.tiles.map((tile, i) => (
                <Flex
                  key={tile.label}
                  flex="1"
                  minW="100px"
                  border="1px solid"
                  borderColor="chalk.300"
                  borderRadius="lg"
                  p={3}
                  direction="column"
                  gap={2}
                >
                  <Box w="24px" h="24px" borderRadius="md" bg={i % 2 === 0 ? "bob.100" : "#E7E1F7"} />
                  <Box>
                    <Text fontSize="xl" fontWeight="800">
                      {tile.value}
                    </Text>
                    <Text fontSize="10px" color="slate.500">
                      {tile.label}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Flex>
            <Flex gap={6} wrap="wrap">
              <Box flex="1" minW="200px">
                <Text fontSize="xs" fontWeight="700" color="slate.500" mb={1.5}>
                  {scene.deadlinesHeading}
                </Text>
                <Flex direction="column" gap={1}>
                  {scene.deadlines.map((d) => (
                    <Text key={d} fontSize="xs">
                      {d}
                    </Text>
                  ))}
                </Flex>
              </Box>
              <Box flex="1" minW="200px">
                <Text fontSize="xs" fontWeight="700" color="slate.500" mb={1.5}>
                  {scene.activityHeading}
                </Text>
                <Flex direction="column" gap={1}>
                  {scene.recentActivity.map((a) => (
                    <Text key={a} fontSize="xs">
                      {a}
                    </Text>
                  ))}
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </>
      );
      break;
  }

  return <DemoChrome demo={demo} scene={scene}>{content}</DemoChrome>;
};

const SceneLayer = ({
  scene,
  demo,
  index,
  total,
  progress,
}: {
  scene: BobScene;
  demo: BobContent["demo"];
  index: number;
  total: number;
  progress: MotionValue<number>;
}) => {
  const reduceMotion = useReducedMotion();
  const step = 1 / total;
  const start = index * step;
  const end = start + step;
  const fadeIn = start + step * 0.15;
  const fadeOut = end - step * 0.15;

  const opacity = useTransform(progress, [start, fadeIn, fadeOut, end], [0, 1, 1, 0]);
  const y = useTransform(progress, [start, fadeIn, fadeOut, end], reduceMotion ? [0, 0, 0, 0] : [24, 0, 0, -24]);
  const scale = useTransform(progress, [start, fadeIn, fadeOut, end], reduceMotion ? [1, 1, 1, 1] : [0.98, 1, 1, 0.98]);

  return (
    <MotionBox position="absolute" inset={0} style={{ opacity, y, scale }} display="flex" flexDirection="column" gap={4}>
      <Box flexShrink={0}>
        <Heading as="h3" fontFamily="heading" fontSize="2xl" mb={2}>
          {scene.title}
        </Heading>
        <Text fontSize="sm" color="slate.700" maxW="560px">
          {scene.description}
        </Text>
      </Box>
      <Box flex="1" minH={0}>
        <ScenePanel scene={scene} demo={demo} />
      </Box>
    </MotionBox>
  );
};

const RailItem = ({
  scene,
  index,
  total,
  progress,
}: {
  scene: BobScene;
  index: number;
  total: number;
  progress: MotionValue<number>;
}) => {
  const step = 1 / total;
  const start = index * step;
  const end = start + step;
  const fadeIn = start + step * 0.15;
  const fadeOut = end - step * 0.15;
  const activeness = useTransform(progress, [start, fadeIn, fadeOut, end], [0.35, 1, 1, 0.35]);

  return (
    <Flex align="center" gap={3} py={2}>
      <MotionBox w="3px" h="20px" bg="bob.300" borderRadius="full" style={{ scaleY: activeness }} />
      <motion.div style={{ opacity: activeness }}>
        <Text fontSize="sm" fontWeight="700" color="ink.900">
          {scene.label}
        </Text>
      </motion.div>
    </Flex>
  );
};

const BobDemo = ({ demo }: { demo: BobContent["demo"] }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ["start start", "end end"] });
  const total = demo.scenes.length;

  return (
    <Box as="section" id="bob-demo" py={{ base: 16, md: 24 }} bg="chalk.200">
      <Reveal textAlign="center" mb={{ base: 10, md: 16 }} maxW="700px" mx="auto" px={5}>
        <Text color="bob.300" fontWeight="bold" fontSize="sm" letterSpacing="wide" mb={3}>
          {demo.eyebrow}
        </Text>
        <Heading as="h2" fontFamily="heading" fontSize={{ base: "3xl", md: "4xl" }} mb={4}>
          {demo.heading}
        </Heading>
        <Text color="slate.700">{demo.subtitle}</Text>
      </Reveal>

      {/* Desktop: pinned, scroll-driven walkthrough */}
      <Box display={{ base: "none", md: "block" }} ref={trackRef} h={`${total * 90}vh`} position="relative">
        {/* Fixed-px top offset (not vh-based): the sticky header's height
            doesn't scale with viewport height, so a vh offset could land
            shorter than the header at low viewport heights and let its
            content peek out from underneath it. */}
        <Box position="sticky" top="104px" h="calc(100vh - 192px)" maxW="1360px" mx="auto" px={{ md: 6, lg: 8 }}>
          <Flex h="100%" gap={{ md: 6, lg: 10 }}>
            <Flex direction="column" w={{ md: "160px", lg: "220px" }} flexShrink={0} justify="center" gap={1}>
              {demo.scenes.map((scene, i) => (
                <RailItem key={scene.id} scene={scene} index={i} total={total} progress={scrollYProgress} />
              ))}
            </Flex>
            <Box flex="1" position="relative" minW={0}>
              <Box position="absolute" inset={0}>
                {demo.scenes.map((scene, i) => (
                  <SceneLayer key={scene.id} scene={scene} demo={demo} index={i} total={total} progress={scrollYProgress} />
                ))}
              </Box>
            </Box>
          </Flex>
        </Box>
      </Box>

      {/* Mobile: stacked, no scroll-jacking */}
      <Flex display={{ base: "flex", md: "none" }} direction="column" gap={8} px={5}>
        {demo.scenes.map((scene) => (
          <Reveal key={scene.id}>
            <Text fontSize="xs" fontWeight="700" color="bob.300" mb={1}>
              {scene.label}
            </Text>
            <Heading as="h3" fontFamily="heading" fontSize="xl" mb={2}>
              {scene.title}
            </Heading>
            <Text fontSize="sm" color="slate.700" mb={4}>
              {scene.description}
            </Text>
            <Box h="380px">
              <ScenePanel scene={scene} demo={demo} />
            </Box>
          </Reveal>
        ))}
      </Flex>
    </Box>
  );
};

export default BobDemo;
